import React, { useMemo, useRef } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Grid,
  Image,
  Space,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconAlertTriangle, IconDownload, IconPlus } from "@tabler/icons-react";
import { get } from "lodash";
import { notifications } from "@mantine/notifications";
import ApprovalTransaction from "@/components/common/approval-transaction/ApprovalTransaction";
import { ApprovalInfo, DocumentInfo } from "@/dto/transfer/response/TransferInfo";
import FileBadge from "@/modules/transfer-money/transfer-international/components/FileBadge";
import { useSupplementIntlTransferDocumentMutation } from "@/services/internationalTransferAPI";
import { RESULT_CODE } from "@/constants/enum";
import DetailGeneric from "../detail-generic/DetailGeneric";
import IconCard from "@/assets/images/pngs/Icon_Card.png";
import {
  formatMoney,
  getCurrencyScale,
} from "@/modules/transfer-money/transfer-international/currencyFormat";
import { useTranslation } from "react-i18next";
import { openStoredDocument } from "@/utils/openDocument";

// Màn chi tiết Chuyển tiền quốc tế (JVC-3369). Hiển thị KHÁC NHAU theo vai trò:
//  - MAKER (không có quyền APPROVE/REJECT/RETURN): layout như màn Xác nhận (step 4),
//    MỌI trường luôn hiển thị (rỗng = "-"), format số tiền "100.00 USD", có badge
//    "Nợ chứng từ bổ sung sau", nút "Bổ sung chứng từ", footer "Thu hồi giao dịch"
//    (Figma 3201-44316).
//  - CHECKER (có quyền duyệt): giữ layout cũ (DetailGeneric) khớp Figma 3166-37100,
//    footer "Hoàn lại / Từ chối / Phê duyệt" (điều khiển tại TransactionDetail).
// Chỉ áp dụng cho businessType = INTERNATIONAL_TRANSFER — không ảnh hưởng loại GD khác.

interface DetailObject {
  key?: string;
  name?: string;
  display?: string;
  value?: string;
}

interface DetailInternationalProps {
  transactionDetailObject?: DetailObject[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transactionDetail?: any;
  approvalInfo?: ApprovalInfo | null;
  documentInfoList?: DocumentInfo[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authModes?: any[];
  /** Phương thức xác thực đang chọn + callback đổi (nối dropdown lên form của TransactionDetail). */
  authType?: string;
  onChangeAuthType?: (value: string) => void;
  /** true = người xem là checker (có quyền duyệt) -> layout cũ khớp Figma checker. */
  isChecker?: boolean;
}

/** Suy đuôi file từ tên file (documentName) → truyền cho FileBadge. */
const getFileExt = (name?: string) => {
  if (!name) return "";
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1) : "";
};

/**
 * amount của giao dịch quốc tế trả về có thể kèm mã tiền tệ (vd "HKD 25.00" hoặc
 * "25.00 HKD"). Tách thành { amount, currency } để DetailGeneric render đúng.
 */
const parseAmountWithCurrency = (raw?: string | number) => {
  const s = String(raw ?? "").trim();
  const lead = s.match(/^([A-Za-z]{3})\s*([\d.,]+)$/);
  if (lead) {
    return { currency: lead[1].toUpperCase(), amount: lead[2].replace(/,/g, "") };
  }
  const trail = s.match(/^([\d.,]+)\s*([A-Za-z]{3})$/);
  if (trail) {
    return { currency: trail[2].toUpperCase(), amount: trail[1].replace(/,/g, "") };
  }
  return { currency: undefined, amount: s };
};

/**
 * Chuẩn hoá số tiền về dạng "100.00 USD" (đơn vị tiền tệ đứng SAU) và áp dụng
 * số chữ số thập phân theo loại tiền (USD/EUR=2, VND/JPY=0, KWD/BHD=3).
 */
const formatCcyAmount = (raw?: string | number) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  // Tách mã tiền tệ (3 chữ cái, trước hoặc sau) và phần số.
  const lead = s.match(/^([A-Za-z]{3})\s+(.+)$/); // "USD 100.00"
  const trail = s.match(/^(.+?)\s+([A-Za-z]{3})$/); // "100.00 USD"
  const ccy = (lead?.[1] || trail?.[2] || "").toUpperCase();
  const numPart = lead?.[2] ?? trail?.[1] ?? s;
  const value = Number(String(numPart).replace(/,/g, ""));
  if (!ccy || !Number.isFinite(value)) return s;
  return `${formatMoney(value, getCurrencyScale(ccy))} ${ccy}`;
};

/** Parse hạn cam kết "dd/MM/yyyy" → Date (đầu ngày). null nếu không hợp lệ. */
const parseCommitmentDate = (s?: string): Date | null => {
  if (!s) return null;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Đọc File → base64 (bỏ tiền tố data:...;base64,). */
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || "");
      const comma = res.indexOf(",");
      resolve(comma >= 0 ? res.slice(comma + 1) : res);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Dòng label - value trong các card thông tin (đồng bộ với màn Xác nhận step 4).
 * labelWidth set -> nhãn cố định bề rộng, giá trị căn trái theo cột; bỏ trống -> value căn phải.
 */
const InfoRow: React.FC<{
  label?: string;
  value?: React.ReactNode;
  valueColor?: string;
  labelWidth?: number;
}> = ({ label, value, valueColor, labelWidth }) => (
  <Flex
    justify={labelWidth ? "flex-start" : "space-between"}
    align="center"
    gap={20}
  >
    <Text
      fz={14}
      c="#6B7280"
      style={labelWidth ? { width: labelWidth, flexShrink: 0 } : { flexShrink: 0 }}
    >
      {label}
    </Text>
    <Text
      fz={14}
      fw={600}
      c={valueColor || "#212936"}
      ta={labelWidth ? "left" : "right"}
    >
      {value}
    </Text>
  </Flex>
);

const DetailInternational: React.FC<DetailInternationalProps> = ({
  transactionDetailObject = [],
  transactionDetail,
  approvalInfo,
  documentInfoList = [],
  authModes = [],
  authType,
  onChangeAuthType,
  isChecker = false,
}) => {
  const { t } = useTranslation();

  const documents: DocumentInfo[] =
    documentInfoList.length > 0
      ? documentInfoList
      : get(transactionDetail, "documentInfoList", []) || [];

  // Chuẩn hoá amount/currency cho DetailGeneric (layout checker).
  const parsed = parseAmountWithCurrency(transactionDetail?.amount);
  const normalizedDetail = transactionDetail
    ? {
        ...transactionDetail,
        amount: parsed.amount,
        currency: transactionDetail.currency || parsed.currency || "USD",
      }
    : transactionDetail;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [supplementDocument, { isLoading: isSupplementing }] =
    useSupplementIntlTransferDocumentMutation();

  const handleDownload = (doc: DocumentInfo) => {
    openStoredDocument(doc?.documentPath);
  };

  // Luồng "Nợ chứng từ bổ sung sau" (SRS KB-15/KB-27) — dữ liệu từ BE JVC-3373:
  // debtDocumentLater: GD có nợ chứng từ. commitmentDate: hạn bổ sung (dd/MM/yyyy).
  // isDocumentSupplemented: đã bổ sung đủ chứng từ chưa.
  const debtDocumentLater = !!get(transactionDetail, "debtDocumentLater", false);
  const commitmentDate = get(transactionDetail, "commitmentDate", "") as string;
  const isDocumentSupplemented = !!get(
    transactionDetail,
    "isDocumentSupplemented",
    false,
  );
  // Còn nợ chứng từ = có nợ + chưa bổ sung đủ.
  const stillOwing = debtDocumentLater && !isDocumentSupplemented;

  // Quá hạn = còn nợ + đã qua ngày cam kết (so đầu ngày hôm nay).
  const isOverdue = useMemo(() => {
    if (!stillOwing) return false;
    const due = parseCommitmentDate(commitmentDate);
    if (!due) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime() > due.getTime();
  }, [stillOwing, commitmentDate]);

  const transId = (get(transactionDetail, "transId", "") ||
    get(transactionDetail, "refId", "")) as string;

  const handleSupplementDocument = () => {
    fileInputRef.current?.click();
  };

  const handleFilesSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // reset để chọn lại cùng file được
    if (files.length === 0) return;
    if (!transId) {
      notifications.show({
        title: t("AN_ERROR_HAS_OCCURRED"),
        message: t("TRANSACTION_CODE_FOR_SUPPLEMENT_UNKNOWN"),
        color: "red",
      });
      return;
    }
    try {
      const docs = await Promise.all(
        files.map(async (f) => ({
          documentType: "",
          documentName: f.name,
          fileType: getFileExt(f.name),
          fileContent: await fileToBase64(f),
        })),
      );
      const res = await supplementDocument({ transId, documents: docs }).unwrap();
      if (get(res, "body.resultCode") === RESULT_CODE.SUCCESS) {
        notifications.show({ message: t("DOCUMENT_SUPPLEMENT_SUCCESSFUL") });
      } else {
        notifications.show({
          title: t("AN_ERROR_HAS_OCCURRED"),
          message: get(res, "body.resultDesc", "") || t("DOCUMENT_SUPPLEMENT_FAILED"),
          color: "red",
        });
      }
    } catch (err) {
      notifications.show({
        title: t("AN_ERROR_HAS_OCCURRED"),
        message: get(err, "data", "") || t("COULD_NOT_SUPPLEMENT_DOCUMENTS"),
        color: "red",
      });
    }
  };

  // Số dư tài khoản nguồn (nếu BE trả) — hiển thị trên card tài khoản (maker).
  const accountBalanceDisplay =
    get(transactionDetail, "availableBalanceDisplay", "") ||
    get(transactionDetail, "fromAvailableBalanceDisplay", "") ||
    "";

  // "Người tạo" trong detailObjectList bị BE compose bằng username (vd SĐT
  // "0334921273"); ưu tiên tên thật từ approvalInfoDetail.createdBy ("Hà Quang Anh").
  const makerName = String(
    get(transactionDetail, "approvalInfoDetail.createdBy", "") || "",
  ).trim();
  const detailObjectFixed = useMemo(() => {
    if (!makerName) return transactionDetailObject;
    return transactionDetailObject.map((r) => {
      const label = String(r?.display || r?.name || r?.key || "").trim();
      return label === "Người tạo" ? { ...r, value: makerName } : r;
    });
  }, [transactionDetailObject, makerName]);

  // Màn detail (checker) đã hiển thị số tiền ở card header lớn phía trên -> bỏ dòng
  // "Số tiền giao dịch" khỏi list để không trùng. BE vẫn trả dòng này trong
  // detailObjectList để màn kết quả sau duyệt (ApproveLockAccountChecker) hiển thị
  // trong list (vị trí thứ 4). Chỉ lọc cho layout checker, không đụng detailMap của maker.
  const detailObjectForChecker = useMemo(
    () =>
      detailObjectFixed.filter(
        (r) =>
          String(r?.display || r?.name || r?.key || "").trim() !==
          t("TRANSACTION_AMOUNT"),
      ),
    [detailObjectFixed],
  );

  // Tra giá trị hiển thị theo nhãn từ detailObjectList (BE compose theo MOBILE_CONFIG).
  const detailMap = useMemo(() => {
    const m: Record<string, string> = {};
    detailObjectFixed.forEach((r) => {
      const k = String(r?.display || r?.name || r?.key || "").trim();
      const v = r?.value;
      if (k && v !== undefined && v !== null && String(v) !== "") {
        m[k] = String(v);
      }
    });
    return m;
  }, [detailObjectFixed]);
  const pick = (...labels: string[]) => {
    const found = labels.find((l) => detailMap[l]);
    return found ? detailMap[found] : "";
  };

  // Giá trị hiển thị cho layout MAKER (ưu tiên detailObjectList theo nhãn, fallback field thô).
  const amountDisplay =
    pick(t("TRANSACTION_AMOUNT")) ||
    get(transactionDetail, "amountDisplay", "") ||
    get(transactionDetail, "amount", "");
  const feeTypeLabel =
    pick(t("FEE_TYPE")) ||
    get(transactionDetail, "feeTypeName", "") ||
    get(transactionDetail, "feeType", "");
  const feeDisplay =
    pick(t("FEE_AMOUNT")) ||
    get(transactionDetail, "feeAmountDisplay", "") ||
    get(transactionDetail, "feeAmount", "");
  const totalDebitDisplay =
    pick(t("TOTAL_DEBITED_AMOUNT"), t("TOTAL_TRANSACTION_AMOUNT")) ||
    get(transactionDetail, "totalAmountDisplay", "") ||
    amountDisplay;
  const detailContent =
    pick(t("TRANSFER_CONTENT_FULL"), t("REMARKS")) ||
    get(transactionDetail, "detailContent", "") ||
    get(transactionDetail, "fromMessage", "");
  const bankNotice =
    pick(t("NOTICE_TO_THE_BANK")) || get(transactionDetail, "bankNotice", "");
  const beneficiarySwift =
    get(transactionDetail, "beneficiarySwiftCode", "") ||
    get(transactionDetail, "beneficiaryBankCode", "");
  const beneficiaryBankName = get(transactionDetail, "beneficiaryBankName", "");
  const beneficiaryBankAddress = get(
    transactionDetail,
    "beneficiaryBankAddress",
    "",
  );
  const purposeTypeLabel =
    pick(t("PURPOSE_TYPE")) || get(transactionDetail, "purposeTypeName", "");
  const purposeDetailLabel =
    pick(t("PURPOSE_OF_MONEY_TRANSFER")) || get(transactionDetail, "purposeDetailName", "");

  const roInputStyles = {
    input: { backgroundColor: "#F9FAFB", color: "#212936" },
  };

  // Danh sách file chứng từ (dùng chung cho cả 2 layout).
  const fileListNode =
    documents.length === 0 ? (
      <Text fz={14} c="#6B7280">
        {t("NO_DOCUMENTS")}
      </Text>
    ) : (
      <Stack spacing={12}>
        {documents.map((doc, idx) => (
          <Flex
            // eslint-disable-next-line react/no-array-index-key
            key={`${doc.documentId ?? doc.documentName ?? ""}-${idx}`}
            justify="space-between"
            align="center"
          >
            <Flex gap={10} align="center">
              <FileBadge fileType={getFileExt(doc.documentName)} size={30} />
              <Text fz={14} c="#157CC1">
                {doc.documentName}
              </Text>
            </Flex>
            <IconDownload
              size={20}
              color="#2A81D0"
              style={{ cursor: "pointer" }}
              onClick={() => handleDownload(doc)}
            />
          </Flex>
        ))}
      </Stack>
    );

  // ============================= CHECKER =============================
  // Giữ layout cũ (DetailGeneric) khớp Figma 3166-37100: card số tiền + tài khoản
  // ghi nợ + danh sách key-value + danh sách file; cột phải = thông tin phê duyệt.
  if (isChecker) {
    return (
      <Grid align="start" justify="center" gutter="xl">
        <Grid.Col sm={12} md={10} lg={8} xl={6}>
          <DetailGeneric
            transaction={detailObjectForChecker}
            transactionDetail={normalizedDetail}
            amountLabel={t("TRANSACTION_AMOUNT")}
            accountLabel={t("DEBIT_ACCOUNT_LABEL")}
            accountAlign="left"
            applyCurrencyScale
          >
            <Text fw={600} fz={14} c="#212936">
              {t("DOCUMENT_FILE_LIST")}
            </Text>
            <Space h={12} />
            {fileListNode}
          </DetailGeneric>
        </Grid.Col>
        <Grid.Col sm={12} md={10} lg={8} xl={4}>
          {approvalInfo && (
            <ApprovalTransaction approvalInfo={approvalInfo} authModes={authModes} value={authType} onChangeAuthType={onChangeAuthType} />
          )}
        </Grid.Col>
      </Grid>
    );
  }

  // ============================= MAKER =============================
  // Layout như màn Xác nhận step 4; mọi trường luôn hiển thị (rỗng = "-").
  return (
    <>
      {/* Cảnh báo quá hạn nộp chứng từ bổ sung (Figma 3201-44306). */}
      {isOverdue && (
        <Flex
          justify="space-between"
          align="center"
          gap={16}
          p={16}
          mb={16}
          sx={{
            backgroundColor: "#FFDAD6",
            border: "1px solid rgba(186, 26, 26, 0.2)",
            borderRadius: 8,
          }}
        >
          <Flex gap={16} align="center">
            <IconAlertTriangle size={20} color="#93000A" style={{ flexShrink: 0 }} />
            <Box>
              <Text fw={700} fz={16} c="#BA1A1A" lh="24px">
                {t("DOCUMENT_SUPPLEMENT_OVERDUE")}
              </Text>
              <Space h={4} />
              <Text fz={14} lh="20px" c="rgba(147, 0, 10, 0.8)">
                {t("OVERDUE_DOCUMENT_WARNING_NOTE")}
              </Text>
            </Box>
          </Flex>
        </Flex>
      )}

      <Grid align="start" justify="center" gutter="xl">
        {/* ===== CỘT TRÁI — Thông tin chuyển tiền ===== */}
        <Grid.Col sm={12} md={10} lg={8} xl={6}>
          <Card shadow="sm" radius="md" p={20}>
            <Text fw={600} fz={16} c="#212936">
              {t("INFORMATION_TRANSFER")}
            </Text>
            <Space h={16} />
            {/* Card tài khoản nguồn (gradient) */}
            <Box
              w="100%"
              p="md"
              sx={{
                borderRadius: 8,
                background:
                  "linear-gradient(279.48deg, #08AAF3 -9.02%, #2A81D0 10.15%, #10436E 99.12%)",
              }}
            >
              <Flex justify="space-between" align="center" gap={12}>
                <Flex gap={12} align="center">
                  <Box
                    sx={{ background: "#FDFDFD", padding: 4, borderRadius: "50%" }}
                  >
                    <Image src={IconCard} width={44} height={44} />
                  </Box>
                  <Stack spacing={2}>
                    <Text c="#fff" fz={16} fw={500}>
                      {get(transactionDetail, "fromAccountNo", "") || "-"}
                    </Text>
                    <Text c="#fff" fz={14}>
                      {get(transactionDetail, "fromAccountFullName", "") || "-"}
                    </Text>
                  </Stack>
                </Flex>
                {accountBalanceDisplay && (
                  <Text c="#fff" fz={16} fw={500} style={{ whiteSpace: "nowrap" }}>
                    {accountBalanceDisplay}
                  </Text>
                )}
              </Flex>
            </Box>
            <Space h={20} />
            <Stack spacing={16}>
              <InfoRow
                label={t("TRANSACTION_AMOUNT")}
                value={formatCcyAmount(amountDisplay) || "-"}
                labelWidth={180}
              />
              <InfoRow
                label={t("FEE_TYPE")}
                value={feeTypeLabel || "-"}
                labelWidth={180}
              />
              <InfoRow
                label={t("FEE_AMOUNT")}
                value={formatCcyAmount(feeDisplay) || "-"}
                labelWidth={180}
              />
              <InfoRow
                label={t("TOTAL_DEBITED_AMOUNT")}
                value={formatCcyAmount(totalDebitDisplay) || "-"}
                valueColor="#2A81D0"
                labelWidth={180}
              />
            </Stack>
            <Space h={20} />
            <Divider />
            <Space h={20} />
            <Stack spacing={16}>
              <Stack spacing={6}>
                <Text fz={14} c="#6B7280">
                  {t("TRANSFER_CONTENT_FULL")}
                </Text>
                <Text fz={14} fw={500} c="#212936">
                  {detailContent || "-"}
                </Text>
              </Stack>
              <Stack spacing={6}>
                <Text fz={14} c="#6B7280">
                  {t("NOTICE_TO_THE_BANK")}
                </Text>
                <Text fz={14} fw={500} c="#212936">
                  {bankNotice || "-"}
                </Text>
              </Stack>
            </Stack>
            <Space h={20} />
            <Divider />
            <Space h={20} />
            <Text fw={600} fz={16} c="#212936">
              {t("BENEFICIARY_BANK")}
            </Text>
            <Space h={12} />
            <Flex gap={16} align="flex-end">
              <TextInput
                label={t("SWIFT_CODE")}
                w={139}
                radius={8}
                readOnly
                styles={roInputStyles}
                value={beneficiarySwift || "-"}
              />
              <TextInput
                label={t("BANK_NAME")}
                w={200}
                radius={8}
                readOnly
                styles={roInputStyles}
                value={beneficiaryBankName || "-"}
              />
              <TextInput
                label={t("ADDRESS")}
                style={{ flex: 1 }}
                radius={8}
                readOnly
                styles={roInputStyles}
                value={beneficiaryBankAddress || "-"}
              />
            </Flex>
          </Card>
        </Grid.Col>

        {/* ===== CỘT PHẢI — Phê duyệt + Chứng từ cung cấp ===== */}
        <Grid.Col sm={12} md={10} lg={8} xl={4}>
          {approvalInfo && (
            <ApprovalTransaction approvalInfo={approvalInfo} authModes={authModes} value={authType} onChangeAuthType={onChangeAuthType} />
          )}
          <Space h={20} />
          <Card shadow="sm" radius="md" p={20}>
            <Text fw={600} fz={16} c="#212936">
              {t("DOCUMENTS_PROVIDED")}
            </Text>
            <Space h={16} />
            <Stack spacing={14}>
              <InfoRow label={t("PURPOSE_TYPE")} value={purposeTypeLabel || "-"} />
              <InfoRow
                label={t("PURPOSE_OF_MONEY_TRANSFER")}
                value={purposeDetailLabel || "-"}
              />
            </Stack>
            <Space h={18} />
            {/* Tiêu đề danh sách file + badge "Nợ chứng từ bổ sung sau" (màu vàng,
                cùng hàng) khi giao dịch có nợ chứng từ — Figma 3201-44316. */}
            <Flex justify="space-between" align="center" gap={12}>
              <Text fw={600} fz={14} c="#212936">
                {t("DOCUMENT_FILE_LIST")}
              </Text>
              {debtDocumentLater && (
                <Box
                  style={{
                    flexShrink: 0,
                    backgroundColor: "#FFF4D0",
                    color: "#991B1B",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: "18px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("DOCUMENTS_OWED_LATER")}
                </Box>
              )}
            </Flex>
            <Space h={12} />
            {fileListNode}

            {stillOwing && (
              <>
                <Space h={16} />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  style={{ display: "none" }}
                  onChange={handleFilesSelected}
                />
                {/* Nút "Bổ sung chứng từ" — căn giữa (Figma 3201-44316). */}
                <Flex justify="center">
                  <Button
                    variant="outline"
                    leftIcon={<IconPlus size={18} />}
                    loading={isSupplementing}
                    onClick={handleSupplementDocument}
                    sx={() => ({
                      width: "fit-content",
                      borderRadius: 8,
                      padding: "8px 24px",
                      borderColor: "#005F9E",
                      color: "#005F9E",
                      backgroundColor: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 14,
                      lineHeight: "20px",
                    })}
                  >
                    {t("SUPPLEMENT_DOCUMENTS")}
                  </Button>
                </Flex>
                <Space h={10} />
                {/* Ghi chú thời hạn bổ sung chứng từ nợ (chính sách Bank). */}
                <Text fz={12} lh="18px" c="#6B7280">
                  {t("DOCUMENT_SUPPLEMENT_DEADLINE_NOTE")}
                </Text>
              </>
            )}
          </Card>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default DetailInternational;
